/**
 * Collecting a generated file — and the reason nothing here ever holds a URL.
 *
 * ── `WP_REPORT_URL_TTL` is 180 seconds, and that is a threat-model number ───
 *
 * `SECURITY_THREAT_MODEL.md` SEC-48 fixes it at 180, deviating deliberately from
 * spec 03 §8's "15 phút" (WP-SEC-03), and **accepted risk AR-7 was retracted on the
 * strength of that number**: somebody looked at a three-minute window, judged the
 * residual risk of a leaked report URL not worth registering, and deleted the entry
 * that used to cover it. Shipping a UI that treats the URL as durable would make the
 * window as wide as the UI's own memory and leave the risk register silent exactly
 * where the cover used to be.
 *
 * What makes the URL dangerous is not its life but its nature: `GET
 * /reports/{id}/download` runs the full 02 §11 chain — permission, company, the
 * caller's **live** scope compared against the scope frozen on the report row — and
 * then redirects to a string that **S3 does not re-check at all**. One HMAC and an
 * expiry. For those 180 seconds the string *is* the authorisation, and a revocation
 * inside the window does not reach it.
 *
 * ── So the model has no `url` member, and that is structural ────────────────
 *
 * `requestDownload` hands the URL to exactly one caller, which navigates to it
 * immediately, and what this module lets a screen keep afterwards is
 * `DownloadState` — `{ status, filename, expiresAt }` and **no url**. A component
 * cannot render a stale link, or a "copy link" button, or a `mailto:` carrying one,
 * because there is no link in the model to reach for. That is the same technique
 * `web/src/incidents/sla.ts` uses to make a countdown on a breached deadline
 * unrenderable rather than merely discouraged.
 *
 * Two consequences worth stating rather than leaving to be inferred:
 *
 *  - **Every press mints a fresh credential**, and every minting increments
 *    `download_count` — the only record anywhere that the file left the product.
 *    Caching a URL across presses would save a round trip and silently stop
 *    recording that a copy was taken.
 *  - **Nothing on the page can expire while it is open.** The credential lives from
 *    the press to the navigation. The question "what does the operator see when the
 *    URL expires" therefore has an answer, and the answer is that the situation does
 *    not arise. What *does* age is the report — `expires_at` is seven days out,
 *    retention removes the object, and the row then reads `status: 'expired'`.
 */

import type { ApiClient } from '../api/client';
import { reportDownloadPath } from '../api/paths';
import type { Report, ReportDownloadGrant } from '../api/types';
import { WorkforceApiError } from '../api/errors';

/**
 * What a screen may remember about a collection that has already happened.
 *
 * Deliberately **not** a `ReportDownloadGrant`. The grant is handled and dropped.
 */
export type DownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly reportId: string }
  | {
      readonly status: 'granted';
      readonly reportId: string;
      readonly filename: string | null;
      /** When the credential that was just spent stops being accepted. A fact, not a link. */
      readonly expiresAt: string;
    }
  | { readonly status: 'refused'; readonly reportId: string; readonly error: WorkforceApiError };

/**
 * The five `wp_reports.status` values, and the one that gets a control.
 *
 * A download control exists **only** for `done`, and for the other four it is
 * **absent rather than disabled**. The difference is not cosmetic and it is not the
 * same argument in each case:
 *
 *  - `pending` / `running` — there is no file yet. A greyed button says "you may not
 *    have this", when the truth is "it is not made yet", and the person's next action
 *    is to wait, not to ask for a permission.
 *  - `failed` — there will never be a file for this row. The remedy is a new request,
 *    which the form above already offers.
 *  - `expired` — there *was* a file and retention deleted it. Nothing anybody presses
 *    brings it back.
 *
 * **The decision is `status`, not the clock.** A screen that compared `expires_at` to
 * `Date.now()` would call a report dead on a laptop whose clock is fast, and would
 * call a genuinely deleted file alive on one whose clock is slow — and either way it
 * would be a second, weaker copy of a judgement the server already published. The
 * same discipline `sla_breached` settled on `/incidents`.
 */
export function canDownload(report: Report): boolean {
  return report.status === 'done';
}

/** How the page navigates to a signed URL. Injected so a test drives it without jsdom. */
export type Navigate = (url: string) => void;

export interface DownloadOutcome {
  readonly state: DownloadState;
  /** True when the row's status was provably stale and the screen should re-read it. */
  readonly staleStatus: boolean;
}

/**
 * Ask for a signed URL, spend it, and keep only the facts.
 *
 * The URL never leaves this function except through `navigate`, which is called
 * **synchronously with it** and is the browser's own address bar in production. It is
 * not returned, not logged, and not put in the state — `api/client.ts` sets
 * `redirect: 'error'` and `openapi.yaml` offers `mode=json` for exactly this client,
 * so the bytes are fetched by a navigation rather than by `fetch`, which is also what
 * makes `Content-Disposition` work.
 *
 * `409 WORKFORCE_REPORT_NOT_READY` and `410 WORKFORCE_REPORT_EXPIRED` come back as
 * `staleStatus: true`: both mean the status this control was derived from is out of
 * date, and the honest response is to render the server's own sentence and **re-read
 * the row**, not to guess which of the five states it moved to.
 *
 * A cross-company id answers `404`, exactly as a non-existent one does, and this
 * function does not tell them apart — because there is nothing to tell apart. A `403`
 * there would confirm the row exists and turn id-guessing into an inventory of
 * another tenant's exports; the server refuses to distinguish them and a client that
 * rendered two different sentences would leak the distinction the server is spending
 * a `404` to hide.
 */
export async function requestDownload(
  client: ApiClient,
  reportId: string,
  navigate: Navigate,
): Promise<DownloadOutcome> {
  try {
    // The `{ data: … }` envelope every route on this build sends, named rather than
    // assumed: a client typed as the bare grant would read `download_url` off the
    // envelope, get `undefined`, and navigate to the string "undefined".
    const envelope = await client.request<{ data: ReportDownloadGrant }>(
      reportDownloadPath(reportId),
    );
    const grant = envelope.data;
    navigate(grant.download_url);
    return {
      state: {
        status: 'granted',
        reportId,
        filename: grant.filename ?? null,
        expiresAt: grant.expires_at,
      },
      staleStatus: false,
    };
  } catch (cause) {
    const error =
      cause instanceof WorkforceApiError
        ? cause
        : new WorkforceApiError({
            code: 'CLIENT_TRANSPORT_FAILED',
            message: cause instanceof Error ? cause.message : 'download failed',
            status: 0,
          });
    return {
      state: { status: 'refused', reportId, error },
      staleStatus:
        error.code === 'WORKFORCE_REPORT_NOT_READY' || error.code === 'WORKFORCE_REPORT_EXPIRED',
    };
  }
}
